
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiLock, FiShoppingCart, FiUserPlus } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  // Nouvelles couleurs de la charte graphique
  const primaryColor = 'text-yellow-400';
  const primaryBg = 'bg-yellow-400';
  const primaryHover = 'hover:bg-yellow-500';
  const primaryFocus = 'focus:ring-yellow-400';
  const primaryBorder = 'border-yellow-400';
  
  const secondaryColor = 'text-gray-400';
  const secondaryBg = 'bg-gray-400';
  const secondaryHover = 'hover:bg-gray-500';
  const secondaryLight = 'bg-gray-100';
  const secondaryBorder = 'border-gray-300';

  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAdmin, user } = useAuth(); // Ajout de user depuis le contexte
  const navigate = useNavigate();

  // Redirection automatique si déjà connecté
  useEffect(() => {
    if (user) {
      // Vérifier le rôle et rediriger en conséquence
      if (user.role && user.role.name !== 'user') {
        navigate('/dashboard');
      } else {
        navigate('/'); // Rediriger vers la page d'accueil pour les clients
      }
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Données envoyées :', credentials.username, credentials.password);
      const result = await login(credentials.username, credentials.password);
      console.log('Résultat de la connexion :', result);

      if (result.success) {
        // Vérifier le rôle de l'utilisateur après connexion réussie
        const userRole = result.user?.role?.name;
        console.log('Rôle de l\'utilisateur :', userRole);
        
        if (userRole && userRole !== 'client') {
          // Rediriger vers le dashboard pour admin/sous-admin
          navigate('/dashboard');
        } else {
          // Rediriger vers la page d'accueil pour les clients
          navigate('/');
        }
      } else {
        setError(result.message || 'Identifiants incorrects');
      }
    } catch (error) {
      setError('Une erreur est survenue lors de la connexion');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour aller à la page d'inscription
  const goToRegister = () => {
    navigate('/register');
  };

  // Fonction pour aller à la page d'accueil
  const goToHome = () => {
    navigate('/');
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-yellow-400/20 via-yellow-300/10 to-gray-50 flex items-center justify-center px-4 py-8`}>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center ${primaryBg} p-4 rounded-full shadow-lg mb-4 transition-transform duration-300 hover:scale-105`}>
            <FiShoppingCart className="text-5xl text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">StelonneMarket</h1>
          <p className={`${secondaryColor} text-sm mt-2 font-medium`}>Électronique & Électroménager</p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Connexion
          </h2>

          {error && (
            <div className={`${secondaryLight} border ${secondaryBorder} text-gray-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2`}>
              <span className={`${primaryColor} font-bold`}>!</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Login
              </label>
              <div className="relative">
                <FiUser className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${secondaryColor}`} />
                <input
                  type="text"
                  name="username"
                  value={credentials.username}
                  onChange={handleChange}
                  required
                  className={`w-full pl-10 pr-4 py-3 ${secondaryBorder} border rounded-xl focus:ring-2 ${primaryFocus} focus:border-transparent transition-all duration-200`}
                  placeholder="Nom, email ou téléphone"
                />
              </div>
              <p className={`text-xs ${secondaryColor} mt-1`}>
                Vous pouvez utiliser votre nom, email ou numéro de téléphone
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <FiLock className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${secondaryColor}`} />
                <input
                  type="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  required
                  className={`w-full pl-10 pr-4 py-3 ${secondaryBorder} border rounded-xl focus:ring-2 ${primaryFocus} focus:border-transparent transition-all duration-200`}
                  placeholder="Entrez votre mot de passe"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full ${primaryBg} ${primaryHover} text-white py-3 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg hover:shadow-xl`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Connexion en cours...
                </div>
              ) : 'Se connecter'}
            </button>
          </form>

          {/* Bouton pour s'inscrire */}
          <div className="mt-6">
            <button
              onClick={goToRegister}
              className={`w-full flex items-center justify-center gap-2 ${secondaryLight} text-gray-700 py-3 rounded-xl font-semibold ${secondaryHover} transition-all duration-200 border ${secondaryBorder} transform hover:scale-[1.02]`}
            >
              <FiUserPlus className={secondaryColor} />
              Créer un compte
            </button>
            <p className={`text-xs ${secondaryColor} text-center mt-2`}>
              Pas encore de compte ? Inscrivez-vous gratuitement
            </p>
          </div>

          {/* Séparateur */}
          <div className="flex items-center my-6">
            <div className={`flex-grow border-t ${secondaryBorder}`}></div>
            <span className={`px-3 ${secondaryColor} text-sm font-medium`}>OU</span>
            <div className={`flex-grow border-t ${secondaryBorder}`}></div>
          </div>

          {/* Bouton retour accueil */}
          <div className="text-center">
            <button
              onClick={goToHome}
              className={`${primaryColor} ${primaryHover} font-semibold flex items-center justify-center gap-2 transition-colors duration-200 hover:underline`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;